import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, MapPin, Users, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Pill, Loader, Empty } from "@/components/Bits";

const CATS = ["all", "policy", "townhall", "workshop", "culture", "sports", "startup"];
const FALLBACK =
  "https://images.unsplash.com/photo-1573152958734-1922c188fba3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxlbmVyZ2V0aWMlMjBjcm93ZCUyMGV2ZW50JTIwZGFya3xlbnwwfHx8fDE3ODYyMDQ5MTR8MA&ixlib=rb-4.1.0&q=85";

const Events = () => {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState(params.get("q") || "");

  useEffect(() => {
    setLoading(true);
    api
      .get("/events", { params: { category: cat, q: q || undefined } })
      .then((r) => setEvents(r.data))
      .finally(() => setLoading(false));
  }, [cat, q]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Directory</span>
          <h1 className="mt-3 font-bebas text-5xl sm:text-6xl tracking-wide uppercase">Events</h1>
        </div>
        <div className="flex items-center gap-2 border border-white/10 px-3 h-11 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
          <Search size={15} className="text-white/40" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setParams(e.target.value ? { q: e.target.value } : {});
            }}
            placeholder="Search events"
            data-testid="events-search"
            className="bg-transparent outline-none text-sm w-56 placeholder:text-white/30"
          />
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 pc-scrollbar">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`events-filter-${c}`}
            className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
              cat === c ? "border-neon text-neon bg-neon/10" : "border-white/12 text-white/50 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : events.length === 0 ? (
        <div className="mt-12">
          <Empty text="No events match this filter yet." testid="events-empty" />
        </div>
      ) : (
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev, i) => (
            <Link
              key={ev.event_id}
              to={`/events/${ev.event_id}`}
              data-testid={`event-card-${i}`}
              className="group border border-white/10 bg-ink-surface hover:border-neon/50 hover:-translate-y-1 transition-[transform,border-color] duration-300"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={ev.cover_image || FALLBACK} alt={ev.title} className="h-full w-full object-cover opacity-70 group-hover:opacity-95 group-hover:scale-105 transition-[opacity,transform] duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-surface to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <Pill tone="neon">{ev.is_paid ? "Ticketed" : "Free"}</Pill>
                </div>
              </div>
              <div className="p-6">
                <Pill>{ev.category}</Pill>
                <h3 className="mt-4 font-outfit text-lg font-bold leading-snug group-hover:text-neon transition-colors">{ev.title}</h3>
                <p className="mt-2 text-sm text-white/50 line-clamp-2">{ev.summary}</p>
                <div className="mt-5 flex flex-wrap gap-4 text-[11px] uppercase tracking-widest text-white/40">
                  <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {new Date(ev.starts_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={13} /> {ev.city}</span>
                  <span className="flex items-center gap-1.5"><Users size={13} /> {ev.attendee_count}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Events;
